// @ts-nocheck
import React, { useState, useEffect, useImperativeHandle, forwardRef } from 'react';
import { Grid, Typography, Divider, makeStyles } from '@material-ui/core';
import { useTranslation } from 'react-i18next';

import NoReenterForm from './forms/NoReenrteredForm';
import OutboundPakForm from './forms/OutbounPakForm';
import InboundPakForm from './forms/InboundPakForm';
import AllowSubdivisionForm from './forms/AllowSubdivisionForm';
import TimeThresholdForm from './forms/TimeThresholdForm';
import AutomationBranchingForm from './forms/AutomationBranchingForm';
import { styles } from './styles';

const useStyles = makeStyles(styles);

const defaultAutomation = {
  noReEntered: false,
  outboundPak: false,
  inboundPak: false,
  allowPakSubdivision: false,
  timeThreshold: false,
  lowTimeThreshold: '',
  highTimeThreshold: '',
  productId: null,
  nextProcessTypeId: null,
  supplierId: null,
  branchingEnabled: false,
  branchingQuestion: '',
  branchingOptions: [
    { label: '', nextProcessTypeId: null },
    { label: '', nextProcessTypeId: null },
  ],
};

const AutomationSection = forwardRef(({
  controls,
  products = {},
  processTypes = {},
  suppliers = {},
  editingId,
  edit,
  isOpen,
}, ref) => {
  const { t } = useTranslation();
  const classes = useStyles();

  const [automation, setAutomation] = useState({
    ...defaultAutomation,
    supplierId: null,
  });
  const [errors, setErrors] = useState({});

  const getBranchingState = () => {
    const branching = controls?.automationBranching;
    if (!branching || !Array.isArray(branching.options)) {
      return {
        branchingEnabled: false,
        branchingQuestion: '',
        branchingOptions: [
          { label: '', nextProcessTypeId: null },
          { label: '', nextProcessTypeId: null },
        ],
      };
    }

    return {
      branchingEnabled: true,
      branchingQuestion: branching.prompt || '',
      branchingOptions: branching.options.length > 0
        ? branching.options.map((option) => ({
          label: option?.label || '',
          nextProcessTypeId: option?.nextProcessTypeId || null,
        }))
        : [
            { label: '', nextProcessTypeId: null },
            { label: '', nextProcessTypeId: null },
          ],
    };
  };

  useEffect(() => {
    if (!isOpen) {
      setAutomation(defaultAutomation);
      setErrors({});
      return;
    }

    setAutomation({
      noReEntered: !!controls?.productId,
      productId: controls?.productId ?? null,
      outboundPak: !!controls?.nextProcessTypeId || !!controls?.automationBranching,
      nextProcessTypeId: controls?.nextProcessTypeId ?? null,
      inboundPak: !!controls?.autoCreateInboundPak,
      allowPakSubdivision: !!controls?.allowPakSubdivision,
      timeThreshold: !!(controls?.lowTimeThreshold || controls?.highTimeThreshold),
      lowTimeThreshold: controls?.lowTimeThreshold ?? '',
      highTimeThreshold: controls?.highTimeThreshold ?? '',
      supplierId: controls?.supplierId ?? null,
      ...getBranchingState(),
    });
  }, [isOpen, controls]);

  useEffect(() => {
    if (!automation.outboundPak && automation.inboundPak) {
      setAutomation((prev) => ({ ...prev, inboundPak: false }));
    }
  }, [automation.outboundPak]);

  const getAvailableProcessTypes = () => {
    const processTypeList = Object.values(processTypes || {}).filter(
      (processType) => processType?.id && processType?.name,
    );
    const processTypeById = Object.fromEntries(
      processTypeList.map((processType) => [Number(processType.id), processType]),
    );
    const currentId = editingId ? Number(editingId) : null;

    const wouldCreateCycle = (candidateId) => {
      if (!currentId) return false;
      const visited = new Set();
      let current = candidateId;
      while (current) {
        if (current === currentId) return true;
        if (visited.has(current)) break;
        visited.add(current);
        current = processTypeById[current]?.nextProcessTypeId;
      }
      return false;
    };

    return processTypeList
      .filter((processType) => {
        const numId = Number(processType.id);
        if (processType.name === 'Recepción') return false;
        if (numId === currentId) return false;
        if (edit && wouldCreateCycle(numId)) return false;
        return true;
      })
      .sort((a, b) => a.name.localeCompare(b.name));
  };

  const getBranchingProcessTypes = () =>
    Object.values(processTypes || {})
      .filter((processType) => processType?.id && processType?.name)
      .sort((a, b) => a.name.localeCompare(b.name));

  const validate = () => {
    const newErrors = {};
    let isValid = true;

    if (automation.noReEntered && !automation.productId) {
      newErrors.noReEntered = t('Debe seleccionar un producto');
      isValid = false;
    }

    if (automation.outboundPak && !automation.nextProcessTypeId) {
      if (!automation.branchingEnabled) {
        newErrors.outboundPak = t('processTypes.modal_automation.errors.next_process_type_required');
        isValid = false;
      }
    }

    if (automation.branchingEnabled) {
      const branchingErrors = { options: [] };
      const options = automation.branchingOptions || [];
      const validOptions = options.filter(
        (option) => option?.label?.trim() && option?.nextProcessTypeId,
      );

      if (!automation.branchingQuestion?.trim()) {
        branchingErrors.prompt = t('La pregunta es obligatoria');
        isValid = false;
      }

      if (validOptions.length < 2) {
        branchingErrors.optionsGeneral = t('Debes definir al menos dos respuestas');
        isValid = false;
      }

      options.forEach((option, index) => {
        const optionErrors = {};

        const touched = option?.label?.trim() || option?.nextProcessTypeId;
        if (!touched) {
          branchingErrors.options[index] = optionErrors;
          return;
        }

        if (!option.label?.trim()) {
          optionErrors.label = t('La respuesta es obligatoria');
          isValid = false;
        }

        if (!option.nextProcessTypeId) {
          optionErrors.nextProcessTypeId = t('Debe seleccionar un proceso siguiente');
          isValid = false;
        }

        branchingErrors.options[index] = optionErrors;
      });

      if (
        branchingErrors.prompt ||
        branchingErrors.optionsGeneral ||
        branchingErrors.options.some((optionError) => Object.keys(optionError || {}).length > 0)
      ) {
        newErrors.branching = branchingErrors;
      }
    }

    if (automation.timeThreshold) {
      const low = Number(automation.lowTimeThreshold);
      const high = Number(automation.highTimeThreshold);
      if (!low || !high || low >= high) {
        newErrors.timeThreshold = t('processTypes.timeThreshold_form.low_must_be_less_than_high_error');
        isValid = false;
      }
    }

    setErrors(newErrors);
    return isValid;
  };

  useImperativeHandle(ref, () => ({
    getPayload: () => {
      const isValid = validate();
      return {
        isValid,
        payload: {
          autoFillOutputProduct: automation.noReEntered,
          productId: automation.noReEntered ? automation.productId : null,
          autoCreateNextProcess: automation.outboundPak,
          nextProcessTypeId:
            automation.outboundPak && !automation.branchingEnabled
              ? automation.nextProcessTypeId
              : null,
          autoCreateInboundPak: automation.outboundPak && automation.inboundPak,
          allowPakSubdivision: automation.allowPakSubdivision,
          lowTimeThreshold: automation.timeThreshold ? Number(automation.lowTimeThreshold) : null,
          highTimeThreshold: automation.timeThreshold ? Number(automation.highTimeThreshold) : null,
          automationBranching:
            automation.outboundPak && automation.branchingEnabled
              ? {
                  prompt: automation.branchingQuestion.trim(),
                  options: (automation.branchingOptions || [])
                    .filter((option) => option?.label?.trim() && option?.nextProcessTypeId)
                    .map((option, index) => ({
                      key: `branch_${index + 1}`,
                      label: option.label.trim(),
                      nextProcessTypeId: option.nextProcessTypeId,
                    })),
                }
              : null,
        },
      };
    },
  }));

  return (
    <Grid item xs={12}>
      <Divider style={{ margin: '16px 0' }} />
      <Typography variant="subtitle2" color="textSecondary" gutterBottom>
        {t('processTypes.modal_automation.title')}
      </Typography>

      <NoReenterForm
        automation={automation}
        onAutomationChange={setAutomation}
        error={errors.noReEntered}
        products={products}
        classes={classes}
      />

      <OutboundPakForm
        automation={automation}
        onAutomationChange={setAutomation}
        error={errors.outboundPak}
        processTypes={getAvailableProcessTypes()}
        classes={classes}
      />

      <AutomationBranchingForm
        automation={automation}
        onAutomationChange={setAutomation}
        error={errors.branching}
        processTypes={getBranchingProcessTypes()}
        classes={classes}
        disabled={!automation.outboundPak}
      />

      <InboundPakForm
        automation={automation}
        onAutomationChange={setAutomation}
        classes={classes}
      />

      <AllowSubdivisionForm
        automation={automation}
        onAutomationChange={setAutomation}
        classes={classes}
      />

      <TimeThresholdForm
        automation={automation}
        onAutomationChange={setAutomation}
        error={errors.timeThreshold}
        classes={classes}
      />
    </Grid>
  );
});

AutomationSection.displayName = 'AutomationSection';

export default AutomationSection;
